import cx from "classnames";
import { useState } from "react";
import removeMd from "remove-markdown";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import { useAppService } from "../../hooks/use-app-service";
import { history } from "../../_config";
import { createPostDetailPageRouteFromSlug } from "../../_pages/post-detail.page";
import { PostService } from "../../_service";
import AnalyticsService, { EventType } from "../../_service/analytics-service";
import { formatDate } from "../../_utils/formatTime-util";
import { ThumbUpIcon } from "../atoms/icons/thumb-up.icon";
import LazyImage from "../atoms/lazy-image";
import IconButton2 from "../icon-button-2-component";
import IconMenu2, { ActionType } from "../icon-menu-2.component";
import UpdatePost from "./edit";
import { PostProcessing } from "./post-card.component";

/**
 * @param {Object} post post object
 * @param {Object} user current user object. If user is null, then user is not logged in
 * @param {Function} updatePost Callback function to update post
 * @param {Function} deletePost Callback function to delete post
 * @description Display minimal post with cover photo
 */
function SquarePostCardComponent({
  post,
  user,
  addToast,
  updatePost = (post) => {},
  deletePost = (post) => {},
}) {
  // Determine if the post is being edited.
  const [isBeingEdit, setIsBeingEdit] = useState(false);

  // Determine if the post is being deleted.
  const [isProcessing, setIsProcessing] = useState(false);
  const [inProgress, setInProgress] = useState(false);

  const { analyticsService } = useAppService();

  // post preferences
  const { hideLikes, hideComment, hideCreator, hideDate } =
    post.preferences ?? {};

  // Display post heading. If post has no title, display post content.
  const heading = post.title
    ? takeOnly55(post.title)
    : post.description
      ? takeOnly55(removeMd(post.description))
      : undefined;

  function takeOnly55(text) {
    if (text.length > 55) {
      return text.substring(0, 70) + "...";
    }
    return text;
  }

  const handleLikeClick = () => {
    if (inProgress || !user) {
      // if already loading or user not logged in, do nothing
      return;
    }

    setInProgress(true); // setLoading

    const promise = post.isLikedByMe
      ? PostService.dislikePost(user, post.id)
      : PostService.likePost(user, post.id);

    promise
      .then(({ post }) => {
        updatePost(post);
        if (!post.isLikedByMe) {
          AnalyticsService.logEvent(
            EventType.Post.name,
            Number(EventType.Post.like)
          );
          // mixpanel event
          const eventData = {
            userType: post.community.myRole,
            groupName: post?.group.name,
            sectionName: post?.tab.name,
            postUrl:
              window.location.origin + createPostDetailPageRouteFromSlug(post),
          };

          analyticsService.track("like-post", eventData);
        }
      })
      .catch((err) => {
        console.error({ err });
      })
      .finally(() => {
        setInProgress(false);
      });
  };
  return (
    <div className="SquarePostCard h-full cursor-pointer bg-card">
      <div
        className="group flex h-full flex-col"
        onClick={() => {
          history.push(createPostDetailPageRouteFromSlug(post));
        }}>
        {post.banner ? (
          <div className="relative">
            <LazyImage src={post.banner} className="flex-none" alt="banner" />
          </div>
        ) : post.images && post.images.length > 0 ? (
          <>
            <LazyImage
              src={post.images[0]}
              style={{
                aspectRatio: "16/9",
                minHeight: "176px",
                maxHeight: "176px",
              }}
              className="flex-none object-cover"
              alt="banner"
            />
          </>
        ) : (
          <div
            className="bg-primary"
            style={{ minHeight: "176px", maxHeight: "176px" }}
          />
        )}
        <div className="flex h-full flex-grow flex-col p-4">
          {heading ? (
            <div className="flex-none break-words text-sm font-semibold text-secondary-foreground">
              {heading}
            </div>
          ) : (
            <div className="text-sm text-secondary-foreground/70">
              No Caption Available
            </div>
          )}
          {/* {!post.title && !heading && post.banner && (
            <div className="pt-2 h-6 flex-none"></div>
          )} */}
          <div className="mt-4 flex justify-between">
            {!hideCreator ? (
              <div className="flex gap-2.5">
                <img
                  src={post.createdBy.picture}
                  alt=""
                  className="h-9 w-9 rounded-full"
                />
                <div className="flex flex-col">
                  <div className="text-xxs flex gap-1.5 font-semibold text-secondary-foreground">
                    <span>{post.createdBy.name}</span>
                    <span className={`${hideDate && "hidden"}`}>
                      {formatDate(post.createdAt)}
                    </span>
                  </div>
                  {/* Likes and Comments */}
                  <div className="mt-2.5 flex items-center gap-2.5">
                    <div
                      className={cx("mr-1 flex items-center", {
                        hidden: hideLikes,
                      })}>
                      <button
                        className={cx("flex h-4 items-center", {
                          "text-primary": post.isLikedByMe,
                          hidden: hideLikes,
                          "mr-2": !hideLikes,
                        })}
                        onClick={handleLikeClick}>
                        <ThumbUpIcon
                          fill={!post.isLikedByMe ? "none" : ""}
                          className={cx(`mb-1 flex items-center text-xs`, {
                            "text-primary": post.isLikedByMe,
                            "text-secondary-foreground": !post.isLikedByMe,
                            hidden: hideLikes,
                          })}
                          stroke={post.isLikedByMe ? "none" : "currentColor"}
                        />
                        <span className="-mt-1 text-xs text-secondary-foreground">
                          &nbsp;
                          {post.likeCount > 0
                            ? post.likeCount === 1
                              ? "1"
                              : post.likeCount
                            : ""}
                        </span>
                      </button>
                    </div>
                    <div
                      className={cx("flex items-center", {
                        hidden: hideComment,
                      })}>
                      <IconButton2
                        onClick={(e) => {
                          history.push(createPostDetailPageRouteFromSlug(post));
                        }}
                        icon="comment-alt"
                        className="-mt-0.5 text-secondary-foreground"
                        label={`${post.commentCount}`}
                        size="xs"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div />
            )}
            <PostMenuAction />
          </div>
          <div className="flex-grow bg-card"></div>
        </div>
      </div>
      <PostProcessing isProcessing={isProcessing} />
    </div>
  );

  function PostMenuAction() {
    if (!user) {
      return null;
    }
    function deletePostHandler() {
      setIsProcessing(true);
      PostService.deletePost(user, post.id)
        .then(() => {
          addToast("Post deleted");
          deletePost(post);
        })
        .catch((error) => {
          console.log(error);
          addToast(error.message, "", ToastTypes.error);
        })
        .finally(() => {
          setIsProcessing(false);
        });
    }

    function bookmarkHandler() {
      (post.isBookmarkedByMe
        ? PostService.removeBookmark(user, post.id)
        : PostService.bookmarkPost(user, post.id)
      )
        .then(() => {
          // bookmark updated, update the post
          updatePost({
            ...post,
            isBookmarkedByMe: !post.isBookmarkedByMe,
          });
          addToast(
            !post.isBookmarkedByMe ? "Bookmark added!" : "Bookmark removed",
            "",
            !post.isBookmarkedByMe ? ToastTypes.success : ToastTypes.info
          );
        })
        .catch((error) => {
          addToast(error.message, "", ToastTypes.error);
        });
    }
    return (
      <>
        <IconMenu2
          className="text-secondary-foreground"
          hideOnEmpty={true}
          dropdownClassName={post.isBookmarkedByMe ? "w-56" : "w-40"}
          actions={[
            {
              label: user && post.createdBy.id === user.id ? "Edit" : "",
              icon: "edit",
              onClick: () => setIsBeingEdit(true),
            },
            {
              label: post.canDelete ? "Delete" : "",
              icon: "trash",
              onClick: () => deletePostHandler(),
              actionType: ActionType.alert,
            },
            {
              // Toggle bookmark
              icon: "bookmark",
              solidIcon: post.isBookmarkedByMe,
              label: post.isBookmarkedByMe ? "Remove Bookmark" : "Bookmark",
              onClick: () => bookmarkHandler(),
            },
          ]}
        />
        {isBeingEdit ? (
          // <UpdatePostCard
          //   postToUpdate={post}
          //   user={user}
          //   updatePost={(post) => {
          //     setIsBeingEdit(false);
          //     updatePost(post);
          //   }}
          //   cancelUpdate={(e) => {
          //     // set is being edit to false
          //     setIsBeingEdit(false);
          //   }}
          // />
          <UpdatePost
            postToUpdate={post}
            active={isBeingEdit}
            setActive={setIsBeingEdit}
            updatePost={updatePost}
          />
        ) : (
          <></>
        )}
      </>
    );
  }
}
const SquarePostCard = SquarePostCardComponent;
/**
 * @param {Object} post post object
 * @param {Object} user current user object. If user is null, then user is not logged in
 * @param {Function} updatePost Callback function to update post
 * @param {Function} deletePost Callback function to delete post
 * @description Display minimal post with cover photo
 */
export default withToast(SquarePostCard);
